import { useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

type Value = string | number | null | undefined;

/**
 * A list's view (search, page, filters) kept in the address, so the back
 * button, reloading and sharing a link all bring back the same view.
 * Empty values are left out of the address.
 */
export function useQueryParams(): [URLSearchParams, (changes: Record<string, Value>, opts?: { replace?: boolean }) => void] {
  const location = useLocation();
  const history = useHistory();
  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const update = (changes: Record<string, Value>, opts: { replace?: boolean } = {}) => {
    // The address right now, not as of the last render, so two updates in a row both count.
    const next = new URLSearchParams(history.location.search);
    Object.keys(changes).forEach((key) => {
      const v = changes[key];
      if (v === null || v === undefined || v === '') next.delete(key);
      else next.set(key, String(v));
    });
    const search = next.toString();
    const to = { pathname: history.location.pathname, search: search ? `?${search}` : '' };
    // Entries made by the old screens keep their view in location.state, which the screens
    // still read as a fallback; navigating (without that state) is how it gets cleared.
    if (to.search === history.location.search && !history.location.state) return;
    if (opts.replace) history.replace(to);
    else history.push(to);
  };
  return [params, update];
}

/** Reads and writes a remembered preference; storage can be unavailable (private windows). */
export const remembered = {
  get(key: string): string | null {
    try { return window.localStorage.getItem(key); } catch (e) { return null; }
  },
  set(key: string, value: string) {
    try { window.localStorage.setItem(key, value); } catch (e) { /* not remembered */ }
  }
};
